// src/components/FormLists/formListsApplyUtils.js

/**
 * formListsApplyUtils
 * -----------------------------------------------------------------------------
 * Optimized apply helpers for Form Lists V2.
 *
 * PURPOSE
 *  • Convert the shared spreadsheet engine's sparse dirty state into safe
 *    Form Lists context writes
 *  • Avoid the legacy Lists behavior of scanning/cleaning every group on save
 *  • Preserve the existing form-local dropdown schema:
 *      dropdownOptions[optionsSetUFI] = [
 *        { optionId, label, value, hint, dep, order }
 *      ]
 *  • Keep dependency persistence in DropdownDependenciesContext
 *  • Keep field label persistence in FieldMetadataContext / FieldEditContext
 *
 * IMPORTANT
 *  • This file does NOT import React.
 *  • This file does NOT import Firebase.
 *  • This file does NOT read browser storage.
 *  • This file does NOT mutate inputs.
 *  • This file does NOT save directly unless callbacks are passed in.
 *
 * ENTERPRISE APPLY MODEL
 *  • Hot edits stay local inside GroupedSpreadsheetCanvas.
 *  • Dirty state tracks changed groups/rows/cells.
 *  • Apply processes dirty groups only.
 *  • Apply writes only changed optionsSetUFI groups.
 *  • Global Save Draft / Publish can later call the same apply boundary before
 *    compression so your existing save/publish flows remain unchanged.
 * -----------------------------------------------------------------------------
 */

import {
  FORM_LISTS_GROUP_ID_KEY,
  FORM_LISTS_ROW_KEY,
} from './formListsGridConfig';

import {
  buildDropdownOptionsForFormListGroup,
  buildFormListGroupIndex,
} from './formListsAdapters';

import {
  canApplyFormListOptionValues,
  isFormListsManagedOptionValuesReadOnly,
} from './formListsGovernanceUtils';

import {
  normalizeString,
  toArraySafe,
} from '../Spreadsheet/groupedSpreadsheetUtils';

/* -------------------------------------------------------------------------- */
/* Dirty-state normalization                                                  */
/* -------------------------------------------------------------------------- */

export const normalizeDirtyGroupIdSet = ({
  dirtyGroupIds = null,
  dirtyState = null,
} = {}) => {
  if (dirtyGroupIds instanceof Set) {
    return new Set(dirtyGroupIds);
  }

  if (Array.isArray(dirtyGroupIds)) {
    return new Set(dirtyGroupIds);
  }

  if (dirtyState?.dirtyGroupIds instanceof Set) {
    return new Set(dirtyState.dirtyGroupIds);
  }

  if (Array.isArray(dirtyState?.dirtyGroupIds)) {
    return new Set(dirtyState.dirtyGroupIds);
  }

  return new Set();
};

export const normalizeDirtyFieldIdSet = ({
  dirtyFieldIds = null,
} = {}) => {
  if (dirtyFieldIds instanceof Set) {
    return new Set(dirtyFieldIds);
  }

  if (Array.isArray(dirtyFieldIds)) {
    return new Set(dirtyFieldIds);
  }

  return new Set();
};

export const countDirtyRows = ({
  dirtyState = null,
} = {}) => {
  const dirtyRowsByGroup = dirtyState?.dirtyRowsByGroup || {};

  return Object.values(dirtyRowsByGroup).reduce((sum, rowSet) => {
    if (rowSet instanceof Set) return sum + rowSet.size;
    if (Array.isArray(rowSet)) return sum + rowSet.length;
    return sum;
  }, 0);
};

export const countDirtyCells = ({
  dirtyState = null,
} = {}) => {
  const dirtyCellsByGroup = dirtyState?.dirtyCellsByGroup || {};

  return Object.values(dirtyCellsByGroup).reduce((groupSum, rowsMap) => {
    return groupSum + Object.values(rowsMap || {}).reduce((rowSum, cellSet) => {
      if (cellSet instanceof Set) return rowSum + cellSet.size;
      if (Array.isArray(cellSet)) return rowSum + cellSet.length;
      return rowSum;
    }, 0);
  }, 0);
};

/* -------------------------------------------------------------------------- */
/* Group filtering                                                            */
/* -------------------------------------------------------------------------- */

export const getDirtyFormListGroups = ({
  groups = [],
  dirtyGroupIds = null,
  dirtyState = null,
  applyAllGroups = false,
} = {}) => {
  const safeGroups = toArraySafe(groups);

  if (applyAllGroups) {
    return safeGroups;
  }

  const dirtySet = normalizeDirtyGroupIdSet({
    dirtyGroupIds,
    dirtyState,
  });

  if (dirtySet.size === 0) {
    return [];
  }

  return safeGroups.filter((group) => {
    const groupId = normalizeString(
      group?.[FORM_LISTS_GROUP_ID_KEY],
      '',
    );

    return groupId && dirtySet.has(groupId);
  });
};

export const isReadOnlyManagedFormListGroup = (group = {}) => (
  isFormListsManagedOptionValuesReadOnly(group)
);

export const canApplyOptionsForGroup = ({
  group,
  includeReadOnlyGroups = false,
} = {}) => {
  if (!group) return false;

  return canApplyFormListOptionValues({
    group,
    includeReadOnlyGroups,
  });
};

/* -------------------------------------------------------------------------- */
/* Option write planning                                                      */
/* -------------------------------------------------------------------------- */

export const buildFormListOptionWriteForGroup = ({
  group,
  includeReadOnlyGroups = false,
} = {}) => {
  if (!canApplyOptionsForGroup({ group, includeReadOnlyGroups })) {
    return null;
  }

  const groupId = normalizeString(group?.[FORM_LISTS_GROUP_ID_KEY], '');
  const fieldId = normalizeString(group?.fieldId, '') || null;
  const optionsSetUFI = normalizeString(group?.optionsSetUFI, '');

  if (!groupId || !optionsSetUFI) {
    return null;
  }

  const nextOptions = buildDropdownOptionsForFormListGroup({
    group,
    generateOptionIdIfMissing: true,
  });

  return {
    groupId,
    fieldId,
    optionsSetUFI,
    nextOptions,
    optionCount: nextOptions.length,
  };
};

export const buildFormListOptionWrites = ({
  groups = [],
  dirtyGroupIds = null,
  dirtyState = null,
  includeReadOnlyGroups = false,
  applyAllGroups = false,
} = {}) => {
  const dirtyGroups = getDirtyFormListGroups({
    groups,
    dirtyGroupIds,
    dirtyState,
    applyAllGroups,
  });

  return dirtyGroups
    .map((group) => buildFormListOptionWriteForGroup({
      group,
      includeReadOnlyGroups,
    }))
    .filter(Boolean);
};

/* -------------------------------------------------------------------------- */
/* Field label write planning                                                 */
/* -------------------------------------------------------------------------- */

const getComparableGroupTitle = (group = {}) => (
  normalizeString(group?.title, '')
);

export const buildFormListFieldLabelWrites = ({
  groups = [],
  baseGroups = [],
  dirtyFieldIds = null,
  includeReadOnlyLabels = false,
} = {}) => {
  const safeGroups = toArraySafe(groups);
  const dirtyFieldSet = normalizeDirtyFieldIdSet({ dirtyFieldIds });

  // If no explicit dirty field labels are supplied and no base snapshot is
  // available, avoid guessing. The first Lists Lab version will focus on
  // option row edits; title editing can opt into this by passing dirtyFieldIds.
  if (dirtyFieldSet.size === 0 && toArraySafe(baseGroups).length === 0) {
    return [];
  }

  const baseIndex = buildFormListGroupIndex(baseGroups);

  return safeGroups.reduce((writes, group) => {
    const groupId = normalizeString(group?.[FORM_LISTS_GROUP_ID_KEY], '');
    const fieldId = normalizeString(group?.fieldId, '');

    if (!groupId || !fieldId) return writes;

    if (dirtyFieldSet.size > 0 && !dirtyFieldSet.has(fieldId)) {
      return writes;
    }

    const isReadOnlyManagedLabel =
      group?.isOrgManaged === true &&
      group?.allowLocalLabelOverride !== true;

    if (isReadOnlyManagedLabel && !includeReadOnlyLabels) {
      return writes;
    }

    const nextTitle = getComparableGroupTitle(group);

    if (!nextTitle) {
      return writes;
    }

    const baseGroup = baseIndex[groupId] || null;
    const previousTitle = baseGroup
      ? getComparableGroupTitle(baseGroup)
      : '';

    if (baseGroup && previousTitle === nextTitle) {
      return writes;
    }

    writes.push({
      groupId,
      fieldId,
      patch: {
        label: nextTitle,
      },
      previousLabel: previousTitle || null,
      nextLabel: nextTitle,
    });

    return writes;
  }, []);
};

/* -------------------------------------------------------------------------- */
/* Apply plan                                                                 */
/* -------------------------------------------------------------------------- */

export const buildFormListsApplyDiagnostics = ({
  groups = [],
  dirtyState = null,
  dirtyGroupIds = null,
  optionWrites = [],
  fieldMetadataWrites = [],
  skippedReadOnlyGroups = [],
  applyAllGroups = false,
} = {}) => {
  const dirtySet = normalizeDirtyGroupIdSet({
    dirtyGroupIds,
    dirtyState,
  });

  return {
    totalGroupCount: toArraySafe(groups).length,
    applyAllGroups: applyAllGroups === true,
    dirtyGroupCount: applyAllGroups ? toArraySafe(groups).length : dirtySet.size,
    dirtyRowCount: countDirtyRows({ dirtyState }),
    dirtyCellCount: countDirtyCells({ dirtyState }),
    optionWriteCount: toArraySafe(optionWrites).length,
    fieldMetadataWriteCount: toArraySafe(fieldMetadataWrites).length,
    skippedReadOnlyGroupCount: toArraySafe(skippedReadOnlyGroups).length,
    dirtyGroupIds: applyAllGroups
      ? toArraySafe(groups)
          .map((group) => normalizeString(group?.[FORM_LISTS_GROUP_ID_KEY], ''))
          .filter(Boolean)
      : Array.from(dirtySet),
  };
};

/**
 * buildFormListsApplyPlan
 * -----------------------------------------------------------------------------
 * Builds a sparse apply plan from current spreadsheet groups.
 *
 * Default behavior:
 *  • process dirty groups only
 *  • skip read-only org-managed option groups
 *  • do not guess label writes unless dirtyFieldIds/baseGroups are supplied
 *
 * This function does not execute writes. It only returns a plan.
 */
export const buildFormListsApplyPlan = ({
  groups = [],
  baseGroups = [],
  dirtyState = null,
  dirtyGroupIds = null,
  dirtyFieldIds = null,
  includeReadOnlyGroups = false,
  includeReadOnlyLabels = false,
  applyAllGroups = false,
} = {}) => {
  const safeGroups = toArraySafe(groups);

  const dirtyGroups = getDirtyFormListGroups({
    groups: safeGroups,
    dirtyGroupIds,
    dirtyState,
    applyAllGroups,
  });

  const skippedReadOnlyGroups = dirtyGroups.filter((group) => (
    isReadOnlyManagedFormListGroup(group) && !includeReadOnlyGroups
  ));

  const optionWrites = buildFormListOptionWrites({
    groups: safeGroups,
    dirtyGroupIds,
    dirtyState,
    includeReadOnlyGroups,
    applyAllGroups,
  });

  const fieldMetadataWrites = buildFormListFieldLabelWrites({
    groups: safeGroups,
    baseGroups,
    dirtyFieldIds,
    includeReadOnlyLabels,
  });

  const diagnostics = buildFormListsApplyDiagnostics({
    groups: safeGroups,
    dirtyState,
    dirtyGroupIds,
    optionWrites,
    fieldMetadataWrites,
    skippedReadOnlyGroups,
    applyAllGroups,
  });

  return {
    status: 'ready',
    optionWrites,
    fieldMetadataWrites,

    // Dependency writes are intentionally absent from this apply layer.
    // The current dependency editor writes through DropdownDependenciesContext.
    dependencyWrites: [],

    diagnostics,
  };
};

export const buildFormListsApplyPlanFromSnapshot = ({
  snapshot,
  baseGroups = [],
  dirtyFieldIds = null,
  includeReadOnlyGroups = false,
  includeReadOnlyLabels = false,
  applyAllGroups = false,
} = {}) => {
  return buildFormListsApplyPlan({
    groups: snapshot?.groups || [],
    baseGroups,
    dirtyState: snapshot?.dirtyState || null,
    dirtyFieldIds,
    includeReadOnlyGroups,
    includeReadOnlyLabels,
    applyAllGroups,
  });
};

/* -------------------------------------------------------------------------- */
/* Apply execution                                                            */
/* -------------------------------------------------------------------------- */

const runMaybeAsync = async (fn, ...args) => {
  if (typeof fn !== 'function') {
    return null;
  }

  return await Promise.resolve(fn(...args));
};

export const applyFormListOptionWrites = async ({
  optionWrites = [],
  saveDropdownOptionsToStorage,
} = {}) => {
  if (typeof saveDropdownOptionsToStorage !== 'function') {
    return {
      status: 'skipped',
      reason: 'missing_saveDropdownOptionsToStorage',
      results: [],
    };
  }

  const results = [];

  for (const write of toArraySafe(optionWrites)) {
    const optionsSetUFI = normalizeString(write?.optionsSetUFI, '');

    if (!optionsSetUFI) {
      results.push({
        status: 'skipped',
        reason: 'missing_optionsSetUFI',
        write,
      });
      continue;
    }

    try {
      await runMaybeAsync(
        saveDropdownOptionsToStorage,
        optionsSetUFI,
        toArraySafe(write?.nextOptions),
      );

      results.push({
        status: 'success',
        groupId: write?.groupId || null,
        fieldId: write?.fieldId || null,
        optionsSetUFI,
        optionCount: toArraySafe(write?.nextOptions).length,
      });
    } catch (error) {
      results.push({
        status: 'error',
        groupId: write?.groupId || null,
        fieldId: write?.fieldId || null,
        optionsSetUFI,
        error,
        message: error?.message || String(error),
      });
    }
  }

  const hasErrors = results.some((result) => result.status === 'error');

  return {
    status: hasErrors ? 'error' : 'success',
    results,
  };
};

export const applyFormListFieldMetadataWrites = async ({
  fieldMetadataWrites = [],
  updateFieldMetadata,
  updateField,
} = {}) => {
  if (
    typeof updateFieldMetadata !== 'function' &&
    typeof updateField !== 'function'
  ) {
    return {
      status: 'skipped',
      reason: 'missing_field_metadata_update_callbacks',
      results: [],
    };
  }

  const results = [];

  for (const write of toArraySafe(fieldMetadataWrites)) {
    const fieldId = normalizeString(write?.fieldId, '');

    if (!fieldId) {
      results.push({
        status: 'skipped',
        reason: 'missing_fieldId',
        write,
      });
      continue;
    }

    try {
      if (typeof updateFieldMetadata === 'function') {
        await runMaybeAsync(updateFieldMetadata, fieldId, write?.patch || {});
      }

      // FieldEditContext keeps the currently selected field panel in sync.
      // This is optional because the lab may not mount FieldEditContext usage
      // immediately, but the callback is supported for parity with current Lists.
      if (typeof updateField === 'function') {
        await runMaybeAsync(updateField, fieldId, write?.patch || {});
      }

      results.push({
        status: 'success',
        fieldId,
        patch: write?.patch || {},
      });
    } catch (error) {
      results.push({
        status: 'error',
        fieldId,
        patch: write?.patch || {},
        error,
        message: error?.message || String(error),
      });
    }
  }

  const hasErrors = results.some((result) => result.status === 'error');

  return {
    status: hasErrors ? 'error' : 'success',
    results,
  };
};

/**
 * applyFormListsApplyPlan
 * -----------------------------------------------------------------------------
 * Executes the sparse apply plan by calling existing context functions.
 *
 * Required for option writes:
 *  • saveDropdownOptionsToStorage(optionsSetUFI, nextOptions)
 *
 * Optional for label writes:
 *  • updateFieldMetadata(fieldId, patch)
 *  • updateField(fieldId, patch)
 *
 * Dependencies are not applied here because current dependency editing should
 * continue flowing through DropdownDependenciesContext.
 */
export const applyFormListsApplyPlan = async ({
  applyPlan,
  saveDropdownOptionsToStorage,
  updateFieldMetadata,
  updateField,
} = {}) => {
  const plan = applyPlan || {
    optionWrites: [],
    fieldMetadataWrites: [],
    diagnostics: {},
  };

  const optionResult = await applyFormListOptionWrites({
    optionWrites: plan.optionWrites,
    saveDropdownOptionsToStorage,
  });

  const fieldMetadataResult = await applyFormListFieldMetadataWrites({
    fieldMetadataWrites: plan.fieldMetadataWrites,
    updateFieldMetadata,
    updateField,
  });

  const hasErrors =
    optionResult.status === 'error' ||
    fieldMetadataResult.status === 'error';

  return {
    status: hasErrors ? 'error' : 'success',
    optionResult,
    fieldMetadataResult,
    dependencyResult: {
      status: 'skipped',
      reason: 'dependencies_are_managed_by_DropdownDependenciesContext',
      results: [],
    },
    diagnostics: plan.diagnostics || {},
  };
};